import React from 'react';
import { TextField, Box, InputAdornment, Select, MenuItem, FormControl } from '@mui/material';
import { styled } from '@mui/material/styles';

interface CountryCode {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

const countryData: CountryCode[] = [
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', dialCode: '+880' },
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dialCode: '+92' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', dialCode: '+971' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦', dialCode: '+974' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dialCode: '+965' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲', dialCode: '+968' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', dialCode: '+60' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', dialCode: '+65' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  
];

const CountrySelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    paddingRight: '8px !important',
    paddingLeft: '8px',
    minWidth: '80px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string, fullNumber: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  margin?: 'normal' | 'dense' | 'none';
  fullWidth?: boolean;
  disabled?: boolean;
  defaultCountry?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  label = 'Phone Number',
  placeholder = '1712345678',
  required = false,
  error = false,
  helperText = '',
  margin = 'normal',
  fullWidth = true,
  disabled = false,
  defaultCountry = 'BD',
}) => {
  // Parse the current value to extract country code and number
  const parsePhoneValue = (phoneValue: string) => {
    if (!phoneValue) return { countryCode: defaultCountry, number: '' };
    
    // If value starts with +, try to match country code
    if (phoneValue.startsWith('+')) {
      for (const country of countryData) {
        if (phoneValue.startsWith(country.dialCode)) {
          return {
            countryCode: country.code,
            number: phoneValue.substring(country.dialCode.length),
          };
        }
      }
    }
    
    // Default to Bangladesh if no country code is found
    return { countryCode: defaultCountry, number: phoneValue.replace(/^\+?88?0?/, '') };
  };

  const { countryCode, number } = parsePhoneValue(value);
  const selectedCountry = countryData.find(c => c.code === countryCode) || countryData[0];

  const handleCountryChange = (newCountryCode: string) => {
    const newCountry = countryData.find(c => c.code === newCountryCode);
    if (newCountry && onChange) {
      // Preserve the selected country even when there is no national number entered.
      // When number is empty we still pass the dial code as the fullNumber so
      // the parent value reflects the selected country and the component
      // can re-derive the selection from the value on the next render.
      const fullNumber = number ? `${newCountry.dialCode}${number}` : newCountry.dialCode;
      onChange(number, fullNumber);
    }
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = event.target.value.replace(/[^\d]/g, ''); // Only allow digits
    if (onChange) {
      const fullNumber = newNumber ? `${selectedCountry.dialCode}${newNumber}` : '';
      onChange(newNumber, fullNumber);
    }
  };

  return (
    <TextField
      fullWidth={fullWidth}
      label={label}
      placeholder={placeholder}
      value={number}
      onChange={handleNumberChange}
      required={required}
      error={error}
      helperText={helperText}
      margin={margin}
      disabled={disabled}
      type="tel"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box sx={{ display: 'flex', alignItems: 'center', borderRight: '1px solid #e0e0e0', pr: 1 }}>
              <FormControl size="small">
                <CountrySelect
                  value={countryCode}
                  onChange={(e) => handleCountryChange(e.target.value as string)}
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  displayEmpty
                  renderValue={(selected) => {
                    const country = countryData.find(c => c.code === selected);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span style={{ fontSize: '16px' }}>{country?.flag}</span>
                        <span style={{ fontSize: '14px', color: '#666' }}>{country?.dialCode}</span>
                      </Box>
                    );
                  }}
                >
                  {countryData.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <span style={{ fontSize: '16px' }}>{country.flag}</span>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                          <span style={{ fontSize: '14px', fontWeight: 500 }}>{country.name}</span>
                          <span style={{ fontSize: '12px', color: '#666' }}>{country.dialCode}</span>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </CountrySelect>
              </FormControl>
            </Box>
          </InputAdornment>
        ),
      }}
      inputProps={{
        maxLength: 15, // Reasonable limit for phone numbers
        style: { paddingLeft: '8px' }
      }}
    />
  );
};

export default PhoneInput;
